import * as THREE from 'three';
import type { PlayerColor } from '../../shared/protocol';

export const PLAYER_SUIT_LABELS: Record<PlayerColor, string> = {
  cyan: 'Acqua saponata',
  orange: 'Ruggine valvola',
  violet: 'Viola vasca',
  green: 'Sapone medico',
};

const SUIT_COLORS: Record<PlayerColor, [number, number]> = {
  cyan: [0x37bcc0, 0x94ebe2],
  orange: [0xd7602b, 0xf0a044],
  violet: [0x8059bd, 0xb89ae4],
  green: [0x4c9c69, 0x9bd58f],
};

export const createPlayerModel = (color: PlayerColor, castShadow = true): THREE.Group => {
  const group = new THREE.Group();
  const [suitColor, highlightColor] = SUIT_COLORS[color] ?? SUIT_COLORS.cyan;
  const suit = new THREE.MeshStandardMaterial({ color: suitColor, roughness: 0.38, metalness: 0.14, flatShading: true });
  const suitHighlight = new THREE.MeshStandardMaterial({ color: highlightColor, emissive: suitColor, emissiveIntensity: 0.18, roughness: 0.34, flatShading: true });
  const dark = new THREE.MeshStandardMaterial({ color: 0x172126, roughness: 0.52, metalness: 0.62, flatShading: true });
  const orange = new THREE.MeshStandardMaterial({ color: 0xe06b2b, emissive: 0x7d250d, emissiveIntensity: 0.18, roughness: 0.38, flatShading: true });
  const visor = new THREE.MeshStandardMaterial({ color: 0xf5c633, emissive: 0xa46916, emissiveIntensity: 0.42, metalness: 0.34, roughness: 0.2, flatShading: true });
  const body = new THREE.Mesh(new THREE.DodecahedronGeometry(0.5, 0), suit);
  body.position.y = 0.92;
  body.scale.set(0.82, 1.12, 0.68);
  const belt = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.08, 5, 10), orange);
  belt.position.y = 0.65;
  belt.rotation.x = Math.PI / 2;
  const helmet = new THREE.Mesh(new THREE.IcosahedronGeometry(0.38, 1), dark);
  helmet.position.y = 1.58;
  const facePlate = new THREE.Mesh(new THREE.CircleGeometry(0.27, 8), visor);
  facePlate.position.set(0, 1.58, -0.29);
  facePlate.rotation.y = Math.PI;
  const visorRim = new THREE.Mesh(new THREE.TorusGeometry(0.29, 0.045, 5, 10), orange);
  visorRim.position.set(0, 1.58, -0.305);
  for (const side of [-1, 1]) {
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.105, 0.13, 0.78, 6), suit);
    arm.position.set(side * 0.47, 1, -0.04);
    arm.rotation.z = side * -0.18;
    const shoulder = new THREE.Mesh(new THREE.DodecahedronGeometry(0.2, 0), side > 0 ? orange : suitHighlight);
    shoulder.position.set(side * 0.48, 1.28, -0.03);
    shoulder.scale.set(1.25, 0.75, 1);
    const boot = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.36, 0.46), dark);
    boot.position.set(side * 0.22, 0.2, -0.08);
    group.add(arm, shoulder, boot);
  }
  const backpack = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.85, 0.34), dark);
  backpack.position.set(0, 1.02, 0.4);
  const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.68, 8), orange);
  tank.position.set(-0.24, 1.03, 0.6);
  const soapTank = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.7, 8), suitHighlight);
  soapTank.position.set(0.24, 1.03, 0.6);
  const weapon = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.15, 0.86, 8), dark);
  weapon.position.set(0.42, 0.95, -0.48);
  weapon.rotation.x = Math.PI / 2;
  const tankGauge = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.035, 5, 8), visor);
  tankGauge.position.set(-0.24, 1.27, 0.72);
  const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.42, 6), orange);
  antenna.position.set(0.2, 1.98, 0.02);
  antenna.rotation.z = -0.18;
  const antennaTip = new THREE.Mesh(new THREE.IcosahedronGeometry(0.07, 0), visor);
  antennaTip.position.set(0.24, 2.18, 0.02);
  const chestBadge = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.05, 8), visor);
  chestBadge.position.set(0, 1.03, -0.38);
  chestBadge.rotation.x = Math.PI / 2;
  group.add(body, belt, helmet, facePlate, visorRim, backpack, tank, soapTank, weapon, tankGauge, antenna, antennaTip, chestBadge);
  group.traverse((node) => {
    if (node instanceof THREE.Mesh) node.castShadow = castShadow;
  });
  return group;
};
